import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import translate from '../../translate';
import Notifications from '../../../services/notifications';
import FormGroup from '../FormGroup';
import FormLabel from '../FormLabel';

const clearErrors = (selectElement, required, name) => {
  if (required) {
    const form = selectElement.closest('.form-group');
    form.removeClass('has-danger').addClass('has-success');
    form.find(`#${name}-error`).remove();
  }
};

function AsyncSelector(props) {
  const {
    t,
    id,
    name,
    url,
    required,
    onChange,
    onUnselect,
    disabled,
    value,
    className,
    multiple,
    hideAll,
    placeholder,
    transformItems,
    showFilter,
    allowClear,
    dropdownParent,
    noneTextOption,
    query,
    displayNameInSelector,
    groupProps,
    label,
    containerClassName,
    tooltip,
    labelClassName,
    hint,
    pageSize,
    headers,
    initialParams,
    onChangeFullObject,
    onHandleResponse,
    onDetailEndpoint,
  } = props;

  if (!url) {
    return null;
  }

  const defaultDisplayName = useCallback(item => item.name, []);
  const displayName = displayNameInSelector || defaultDisplayName;

  // region Fetch individual item
  const fetchItem = useCallback(
    async detailEndpoint => {
      try {
        const { data: response } = await axios.get(detailEndpoint, {
          params: initialParams,
          headers,
        });
        return response.data;
      } catch (err) {
        console.error(err);
        return null;
      }
    },
    [url, headers],
  );
  // endregion Fetch individual item

  // region Set Items
  const setValue = useCallback(async () => {
    const $selectorElement = $(`#${id}`);
    if (value && value.length) {
      const itemsToFetch = (typeof value === 'string' ? [value] : value).filter(
        itemId => !$selectorElement.find(`option[value='${itemId}']`).length,
      );
      if (value !== 'none') {
        try {
          const items = itemsToFetch ? await fetchItem(onDetailEndpoint) : [];

          items.forEach(item => {
            const optionItem = new Option(
              item._id === 'none' ? noneTextOption : displayName(item),
              item._id,
              false,
              true,
            );
            $selectorElement.append(optionItem);
          });
          $selectorElement.trigger('change');
        } catch (err) {
          console.error(err);
          const { error } = err?.responseJSON || {};
          if (error) {
            Notifications.showNotificationError(t('error'), t(error));
          }
        }
      } else {
        itemsToFetch.forEach(item => {
          const optionUser = new Option(
            item._id === 'none' ? noneTextOption : displayName(item),
            item._id,
            false,
            true,
          );
          $selectorElement.append(optionUser);
        });
        $selectorElement.trigger('change');
      }
    }
  }, [initialParams, value]);
  // endregion Set Items

  useEffect(() => {
    const selectElement = $(`#${id}`);

    const defaultPlaceholder = multiple ? t('select.items') : t('select.item');
    const select2Options = {
      allowClear,
      multiple,
      placeholder: placeholder || defaultPlaceholder,
      width: '100%',
      ajax: {
        url,
        headers,
        query,
        data: params => {
          const page = params.page || 1;
          return {
            search: params?.term?.length > 0 ? params.term : undefined,
            page,
            index: (page - 1) * pageSize,
            offset: pageSize,
            query: query || {},
          };
        },
        dataType: 'json',
        delay: 200,
        processResults: res => {
          let results = onHandleResponse
            ? onHandleResponse(res.data)
            : res.data.map(item => ({
                id: item._id,
                text: item._id === 'none' ? noneTextOption : displayName(item),
                item,
              }));
          if (transformItems) {
            results = transformItems(results);
          }

          const pagination = {
            more:
              res?.data?.length > 0
                ? res?.meta?.page + pageSize < res?.meta?.total
                : false,
          };
          return { results, pagination };
        },
      },
    };
    if (dropdownParent) {
      select2Options.dropdownParent = $(dropdownParent);
    }
    selectElement.select2(select2Options);

    selectElement.on('select2:select', evt => {
      clearErrors(selectElement, required, name);
      onChange(onChangeFullObject ? evt.params.data : evt.params.data.id);
    });

    if (!hideAll) {
      const optionAll = new Option(t('all'), 'all', false, true);
      selectElement.append(optionAll).trigger('change');
    }

    // Handle this case when we have allowClear=true on single selection mode
    if (!multiple && allowClear) {
      selectElement.on('select2:unselecting', () => {
        clearErrors(selectElement, required, name);
        onChange(null);
      });
    }

    if (onUnselect) {
      selectElement.on('select2:unselect', evt => {
        clearErrors(selectElement, required, name);
        onUnselect(evt.params.data.id);
      });
    }

    if (disabled) {
      selectElement.select2('enable', false);
    }

    return () => selectElement.select2('destroy');
  }, []);

  useEffect(() => {
    if (value) {
      setValue();
    } else {
      $(`#${id}`).val(null).trigger('change');
    }
  }, []);

  const finalClassName = showFilter
    ? `select-show-filter ${className}`
    : className;

  return (
    <>
      <FormGroup {...groupProps}>
        {label && (
          <FormLabel
            id={id}
            label={label}
            tooltip={tooltip}
            className={labelClassName}
          />
        )}
        <div className={containerClassName}>
          {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
          <select
            id={id}
            name={name || id}
            value={value || ''}
            className={`m-bootstrap-select m_selectpicker ${finalClassName}`}
            data-actions-box="true"
            multiple={multiple}
          />
          {hint && <span className="m-form__help">{hint}</span>}
        </div>
      </FormGroup>
    </>
  );
}

AsyncSelector.propTypes = {
  t: PropTypes.func.isRequired,
  /** Selector id used by the jQuery libraries */
  id: PropTypes.string.isRequired,
  /** Selector html name, required for validation */
  name: PropTypes.string,
  /** Class names to be added to the default ones */
  className: PropTypes.string,
  /** initial value */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  /** onChange handler */
  onChange: PropTypes.func.isRequired,
  /** onUnselect */
  onUnselect: PropTypes.func,
  /** Whether the selector is required in the form, used to display validation on change */
  required: PropTypes.bool,
  /** Allow multiple choices */
  multiple: PropTypes.bool,
  disabled: PropTypes.bool,
  hideAll: PropTypes.bool,
  placeholder: PropTypes.string,
  transformItems: PropTypes.func,
  showFilter: PropTypes.bool,
  url: PropTypes.string,
  allowClear: PropTypes.bool,
  dropdownParent: PropTypes.string,
  noneTextOption: PropTypes.string,
  query: PropTypes.shape({
    users: PropTypes.arrayOf(PropTypes.string),
  }),
  displayNameInSelector: PropTypes.func,
  groupProps: PropTypes.string,
  label: PropTypes.string,
  containerClassName: PropTypes.string,
  tooltip: PropTypes.string,
  labelClassName: PropTypes.string,
  hint: PropTypes.string,
  pageSize: PropTypes.number,
  headers: PropTypes.shape({
    'Accept-Version': PropTypes.string,
    'X-Gamification-API': PropTypes.string,
  }),
  onChangeFullObject: PropTypes.bool,
  initialParams: PropTypes.shape({}),
  onHandleResponse: PropTypes.func,
  onDetailEndpoint: PropTypes.func,
};

AsyncSelector.defaultProps = {
  value: null,
  url: null,
  name: null,
  className: '',
  required: false,
  onUnselect: undefined,
  multiple: false,
  disabled: false,
  hideAll: true,
  placeholder: null,
  transformItems: null,
  showFilter: false,
  allowClear: false,
  dropdownParent: null,
  noneTextOption: '(None)',
  query: null,
  displayNameInSelector: null,
  groupProps: null,
  label: null,
  containerClassName: null,
  tooltip: null,
  labelClassName: null,
  hint: null,
  pageSize: 20,
  headers: {
    'Accept-Version': '1.0.0',
    'X-Gamification-API': 'Games',
  },
  onChangeFullObject: false,
  initialParams: {},
  onHandleResponse: null,
  onDetailEndpoint: null,
};

export default translate(AsyncSelector);
